const triggerSelector = '.ai-job-progress-issue-trigger';
const shellSelector = '.ai-job-progress-shell';

function closeShell(shell: HTMLElement): void {
	const trigger = shell.querySelector<HTMLButtonElement>(triggerSelector);
	const tooltip = shell.querySelector<HTMLElement>('.ai-job-progress-tooltip');
	shell.classList.remove('is-issue-open');
	if (trigger) trigger.setAttribute('aria-expanded', 'false');
	if (tooltip) tooltip.hidden = true;
}

function openShell(shell: HTMLElement, expanded: boolean): void {
	const trigger = shell.querySelector<HTMLButtonElement>(triggerSelector);
	const tooltip = shell.querySelector<HTMLElement>('.ai-job-progress-tooltip');
	shell.classList.toggle('is-issue-open', expanded);
	if (trigger) trigger.setAttribute('aria-expanded', String(expanded));
	if (tooltip) tooltip.hidden = false;
}

function closeAll(root: HTMLElement, except?: HTMLElement): void {
	for (const shell of root.querySelectorAll<HTMLElement>(shellSelector)) {
		if (shell !== except) closeShell(shell);
	}
}

/**
 * Owns the one-line progress issue tooltip. Capture-phase handling keeps its
 * button from bubbling into the job card's delegated detail-navigation click.
 */
export function attachAiProgressIssueTooltipController(root: HTMLElement): () => void {
	const onClick = (event: MouseEvent) => {
		const target = event.target as HTMLElement | null;
		const trigger = target?.closest<HTMLButtonElement>(triggerSelector);
		if (trigger && root.contains(trigger)) {
			event.preventDefault();
			event.stopPropagation();
			const shell = trigger.closest<HTMLElement>(shellSelector);
			if (!shell) return;
			const nextOpen = trigger.getAttribute('aria-expanded') !== 'true';
			closeAll(root, shell);
			if (nextOpen) openShell(shell, true);
			else closeShell(shell);
			return;
		}
		if (!target?.closest(shellSelector)) closeAll(root);
	};

	const onMouseOver = (event: MouseEvent) => {
		const trigger = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>(triggerSelector);
		const shell = trigger?.closest<HTMLElement>(shellSelector);
		if (shell && trigger?.getAttribute('aria-expanded') !== 'true') openShell(shell, false);
	};

	const onMouseOut = (event: MouseEvent) => {
		const shell = (event.target as HTMLElement | null)?.closest<HTMLElement>(shellSelector);
		const nextTarget = event.relatedTarget as Node | null;
		if (shell && nextTarget && shell.contains(nextTarget)) return;
		if (shell) closeShell(shell);
	};

	const onFocusIn = (event: FocusEvent) => {
		const trigger = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>(triggerSelector);
		const shell = trigger?.closest<HTMLElement>(shellSelector);
		if (shell && trigger?.getAttribute('aria-expanded') !== 'true') openShell(shell, false);
	};

	const onFocusOut = (event: FocusEvent) => {
		const shell = (event.target as HTMLElement | null)?.closest<HTMLElement>(shellSelector);
		if (!shell) return;
		queueMicrotask(() => {
			if (!shell.contains(document.activeElement)) {
				closeShell(shell);
			}
		});
	};

	const onScroll = () => closeAll(root);

	const onKeydown = (event: KeyboardEvent) => {
		if (event.key !== 'Escape') return;
		const shell = (event.target as HTMLElement | null)?.closest<HTMLElement>(shellSelector)
			?? root.querySelector<HTMLElement>(`${shellSelector}.is-issue-open`);
		if (!shell) return;
		event.preventDefault();
		closeAll(root);
		shell.querySelector<HTMLButtonElement>(triggerSelector)?.focus();
		closeShell(shell);
	};

	root.addEventListener('click', onClick, true);
	root.addEventListener('mouseover', onMouseOver);
	root.addEventListener('mouseout', onMouseOut);
	root.addEventListener('focusin', onFocusIn);
	root.addEventListener('focusout', onFocusOut);
	root.addEventListener('keydown', onKeydown);
	document.addEventListener('scroll', onScroll, true);
	return () => {
		root.removeEventListener('click', onClick, true);
		root.removeEventListener('mouseover', onMouseOver);
		root.removeEventListener('mouseout', onMouseOut);
		root.removeEventListener('focusin', onFocusIn);
		root.removeEventListener('focusout', onFocusOut);
		root.removeEventListener('keydown', onKeydown);
		document.removeEventListener('scroll', onScroll, true);
	};
}
